import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/useLanguage";
import { useAuth } from "@/contexts/useAuth";
import { ActiveSessionDialog } from "./ActiveSessionDialog";
import { DeviceVerificationDialog } from "./DeviceVerificationDialog";
import { SubscriptionRequiredDialog } from "./SubscriptionRequiredDialog";

const isDev = import.meta.env.DEV;

interface Device {
  id: string;
  device_name: string;
  device_type: string;
  ip_address: string | null;
  last_seen: string;
}

interface VerificationData {
  email: string;
  deviceFingerprint: string;
}

interface TempSession {
  access_token: string;
  refresh_token: string;
}

export const LoginForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showActiveSessionDialog, setShowActiveSessionDialog] = useState(false);
  const [activeDevices, setActiveDevices] = useState<Device[]>([]);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [verificationData, setVerificationData] = useState<VerificationData | null>(null);
  const [tempSession, setTempSession] = useState<TempSession | null>(null);
  const [isInVerificationFlow, setIsInVerificationFlow] = useState(false);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [subscriptionUserEmail, setSubscriptionUserEmail] = useState<string>("");
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, userRole, signIn, cleanupTempSession } = useAuth();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();

  // Check for email confirmation success
  useEffect(() => {
    if (searchParams.get('emailConfirmed') === 'true') {
      toast({
        title: t("auth.login.toast.emailConfirmed.title"),
        description: t("auth.login.toast.emailConfirmed.desc"),
      });
      // Remove the query parameter from URL
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, t, toast]);

  const performSignIn = async (forceLogin = false) => {
    setIsLoading(true);

    try {
      if (isDev) console.log('LoginForm: Starting sign in process...', { email, forceLogin });
      const result = await signIn(email, password, forceLogin);
      if (isDev) console.log('LoginForm: Sign in result received:', result);

      // Check if device verification is required first
      if (result.requiresVerification && result.verificationData) {
        if (isDev) console.log('LoginForm: Verification required, showing dialog');
        setIsInVerificationFlow(true);
        setVerificationData(result.verificationData);
        setTempSession(result.tempSession);
        // Store temp session token for verification flow restoration
        if (result.tempSession && result.tempSession.access_token) {
          sessionStorage.setItem('device_verification_token', result.tempSession.access_token);
        }
        setShowVerificationDialog(true);
        setIsLoading(false);
        return;
      }

      // Check if subscription is required
      if (result.requiresSubscription) {
        if (isDev) console.log('LoginForm: Subscription required, showing payment gateway');
        setSubscriptionUserEmail(result.userEmail || email);
        setShowSubscriptionDialog(true);
        setIsLoading(false);
        return;
      }

      // Check if force login is required (for existing sessions)
      if (result.requiresForceLogin) {
        if (isDev) console.log('LoginForm: Force login required, showing active session dialog');
        // Convert ActiveDevice[] to Device[]
        const devices: Device[] = (result.activeDevices || []).map(device => ({
          id: device.id,
          device_name: device.device_info?.name || 'Unknown Device',
          device_type: device.device_info?.type || 'Unknown',
          ip_address: null,
          last_seen: device.last_active
        }));
        setActiveDevices(devices);
        setShowActiveSessionDialog(true);
        setIsLoading(false);
        return;
      }

      if (result.error) {
        if (isDev) console.log('LoginForm: Sign in error:', result.error);
        toast({
          title: t("auth.login.toast.error.title"),
          description: result.error.message,
          variant: "destructive",
        });
        return;
      }

      if (isDev) console.log('LoginForm: Sign in successful');
      toast({
        title: t("auth.login.toast.success.title"),
        description: t("auth.login.toast.success.desc"),
      });

      // Redirect to auth-redirect for role-based routing
      navigate('/auth-redirect');
      
    } catch (error) {
      if (isDev) console.error('LoginForm: Unexpected error:', error);
      toast({
        title: t("auth.login.toast.error.title"),
        description: t("auth.login.toast.error.desc"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performSignIn(false);
  };

  const handleForceLogin = async () => {
    setShowActiveSessionDialog(false);
    await performSignIn(true);
  };

  const handleProceedVerification = async () => {
    if (isDev) console.log('LoginForm: User proceeded with verification');
    setShowVerificationDialog(false);
    
    // Show prominent notification with custom styling for visibility
    toast({
      title: t("auth.login.toast.verificationSent.title"),
      description: t("auth.login.toast.verificationSent.desc"),
      duration: 8000,
      className: "fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-96 border-2 border-primary bg-background shadow-lg",
    });
    
    // Navigate to verification page
    const params = new URLSearchParams({
      email: verificationData!.email,
      fingerprint: verificationData!.deviceFingerprint
    });
    navigate(`/device-verification?${params.toString()}`);
  };

  const handleCancelVerification = async () => {
    if (isDev) console.log('LoginForm: User cancelled verification, cleaning up temp session');
    setShowVerificationDialog(false);
    setIsInVerificationFlow(false);
    setVerificationData(null);
    setTempSession(null);
    
    // Clean up the temporary session
    await cleanupTempSession();
  };

  // Only redirect if user is fully authenticated AND not in verification flow
  useEffect(() => {
    if (user && userRole && !isInVerificationFlow && !showVerificationDialog && !verificationData) {
      if (isDev) console.log('LoginForm: User authenticated and not in verification flow, redirecting...');
      const redirectPath = userRole === 'admin' ? '/admin/dashboard' : '/dashboard';
      navigate(redirectPath, { replace: true });
    }
  }, [user, userRole, navigate, isInVerificationFlow, showVerificationDialog, verificationData]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-trust p-4">
      <Card className="w-full max-w-md shadow-elegant border-border/50">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-primary/10 p-1">
              <img 
                src="/assets/icon.png" 
                alt="NostriMed Logo" 
                className="w-full h-full object-contain rounded-full"
                width="64"
                height="64"
                loading="lazy"
              />
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-foreground">{t("auth.login.title")}</CardTitle>
            <CardDescription className="text-muted-foreground">
              {t("auth.login.subtitle")}
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="doctor@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="transition-all duration-300 focus:shadow-medical"
                />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">{t("auth.password")}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10 transition-all duration-300 focus:shadow-medical"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Link
                to="/forgot-password"
                className="text-sm text-primary hover:text-primary-glow transition-colors"
              >
                {t("auth.forgotPassword")}
              </Link>
            </div>

            <Button
              type="submit"
              variant="hero"
              size="lg"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? t("auth.login.signingIn") : t("auth.signIn")}
            </Button>
          </form>

          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              {t("auth.noAccount")}{" "}
              <Link
                to="/signup"
                className="text-primary hover:text-primary-glow transition-colors font-medium"
              >
                {t("nav.signup")}
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>

      <ActiveSessionDialog
        isOpen={showActiveSessionDialog}
        onClose={() => setShowActiveSessionDialog(false)}
        onForceLogin={handleForceLogin}
        devices={activeDevices}
        isLoading={isLoading}
      />

      <DeviceVerificationDialog
        isOpen={showVerificationDialog}
        onClose={handleCancelVerification}
        onProceed={handleProceedVerification}
        isLoading={isLoading}
      />

      <SubscriptionRequiredDialog
        isOpen={showSubscriptionDialog}
        onClose={() => setShowSubscriptionDialog(false)}
        userEmail={subscriptionUserEmail}
      />
    </div>
  );
};
